/**
 * The shape of a thing you can operate, written once.
 *
 * Every text field, picker and select in this application is the same object:
 * the page's own surface, one hairline round it, and a thumb's height. It is
 * here rather than repeated down each form because the alternative was tried —
 * the class list was written out at each input, and the filter bar and the
 * registration form drifted into two different-looking sets of controls on two
 * screens of the same application.
 *
 * **They were underlines**, on the argument that a form should be quiet enough
 * to let the drinking-window band carry the screen. Quiet was the right aim and
 * an underline was the wrong means: on a flat ground a rule under a word reads
 * as a heading with a rule under it, a placeholder sits exactly where a typed
 * value sits and looks like one, and fifteen of them down a page give the eye
 * nothing to hold on to — the form ran together into one column of grey. Quiet
 * is now carried by size and colour instead, and the band is still the only
 * saturated thing on any screen.
 *
 * The height comes from the hand rather than the eye: this is used standing in
 * front of a cabinet, and 44 pixels is the smallest target a thumb hits without
 * aiming.
 */
export const control =
  "bg-surface h-11 w-full rounded-sm border px-3 text-base " +
  "outline-none transition-colors focus:border-accent";

/**
 * The resting border, kept out of `control` deliberately.
 *
 * `border-line` and `border-accent` are two utilities of equal specificity, so
 * which one wins is decided by their order in the generated stylesheet and not
 * by their order in the class attribute. Written as a pair of alternatives,
 * only one of them is ever on the element, and the question does not arise.
 */
export const resting = "border-line text-ink";

/** Doing something — narrowing a list, or carrying an answer. Brass, like
 *  everything else in this interface that acts. */
export const narrowed = "border-accent text-accent";

/** The word above a control. Quiet, and never the size of its own value. */
export const fieldLabel = "text-muted block text-sm";

/** A button that is an action rather than a link: the second half of one
 *  gesture, shaped like the field it sits beside. */
export const actionButton =
  "border-accent text-accent hover:bg-raised h-11 shrink-0 rounded-sm border px-4 " +
  "text-sm transition-colors disabled:opacity-40";

/**
 * The chevron, drawn rather than left to the platform.
 *
 * A native select's arrow is the system's grey in the system's shape, and it is
 * the one mark on these screens that nobody here chose. `currentColor` is what
 * makes it turn brass with the control it belongs to.
 */
export function Chevron() {
  return (
    <svg
      viewBox="0 0 10 6"
      aria-hidden="true"
      className="pointer-events-none absolute top-1/2 right-3 h-1.5 w-2.5 -translate-y-1/2"
    >
      <path
        d="M1 1l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A select with our own arrow on it.
 *
 * `active` is what the control says about itself: on the filter bar it means
 * *this one is holding rows back*, and on a form it is left off, because a
 * field that is merely filled in is not doing anything to anybody.
 */
export function Select({
  id,
  value,
  onChange,
  active = false,
  children,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`relative ${active ? "text-accent" : ""}`}>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${control} appearance-none pr-9 ${active ? narrowed : resting}`}
      >
        {children}
      </select>
      <Chevron />
    </div>
  );
}
